import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavigationProps {
  onCartClick?: () => void;
  cartItemCount?: number;
}

export default function Navigation({ onCartClick, cartItemCount = 0 }: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // If we navigated to home with a hash, scroll to the section
  useEffect(() => {
    if (location.pathname === '/' && location.hash === '#classes') {
      setTimeout(() => {
        const el = document.getElementById('classes');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const handleClassesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      const el = document.getElementById('classes');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#classes');
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop' },
    { name: 'Our Story', path: '/about' },
    { name: 'Classes', path: '/#classes', onClick: handleClassesClick },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
    }`}>
      <div className="px-6 lg:px-12 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-10 h-10 flex items-center justify-center bg-sage-500 rounded-full">
              <i className="ri-leaf-line text-xl text-cream-100"></i>
            </div>
            <span className={`font-serif text-2xl font-semibold transition-colors ${
              isScrolled ? 'text-sage-600' : 'text-sage-600'
            }`}>
              Bodhira
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={link.onClick}
                className={`font-sans text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === link.path
                    ? 'text-sage-700'
                    : isScrolled
                      ? 'text-sage-600 hover:text-sage-800'
                      : 'text-sage-600 hover:text-sage-800'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Cart & Mobile Menu */}
          <div className="flex items-center gap-4">
            <button
              onClick={onCartClick}
              className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-sage-50 transition-colors cursor-pointer"
            >
              <i className={`ri-shopping-cart-line text-xl ${
                isScrolled ? 'text-gray-700' : 'text-gray-700'
              }`}></i>
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-sage-500 text-cream-100 rounded-full text-xs font-sans font-medium flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-sage-50 transition-colors cursor-pointer"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${
                isScrolled ? 'text-gray-700' : 'text-gray-700'
              }`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 py-4 border-t border-sage-200 bg-white/90 backdrop-blur-md rounded-2xl px-4 shadow-lg">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={link.onClick ?? (() => setIsMobileMenuOpen(false))}
                  className={`font-sans text-base font-medium transition-colors cursor-pointer ${
                    location.pathname === link.path
                      ? 'text-sage-600'
                      : 'text-gray-700 hover:text-sage-600'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
